import { ErrorIcon } from "evergreen-ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import { EmptyState } from "../../common/EmptyState";
import { MultiTabView } from "../../common/multitab/MultiTabView";
import { TabInfo } from "../../common/multitab/TabInfo";
import { useProductListingQuery } from "../../datastore/ProductListingDataStore";
import { queryCostMetadataByShopifyProductId } from "../../datastore/CostMetadataDataStore";
import { queryShopifyProductByProductListingId } from "../../datastore/ShopifyProductDataStore";
import { useUserContext } from "../../context/UserContext";
import { NavigationHelper } from "../../navigation/PrevLocationState";
import { userIsAuthorized } from "../../rampart/RampartUtils";
import { CostMetadataTab } from "./CostMetadataTab";
import { PayoutRuleMappingTab } from "./PayoutRuleMappingTab";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";

type Params = { productListingId: string; focusedTab?: ProductTabIds };

const PRODUCT_ROUTE_PATH = "/products/:productListingId/:focusedTab?";

const EDIT_IN_SHOPIFY_BASE_URL = "https://admin.shopify.com/store/";

// TODO: Add productVariants tab in follow-up PR
enum ProductTabIds {
    costMetadata = "cost-metadata",
    payoutRules = "payout-rules",
    editInShopify = "edit-in-shopify",
}

const MultiTabProductPageInternal = () => {

    const history = useHistory();
    const { productListingId, focusedTab } = useParams<Params>();
    const { user } = useUserContext();
    const isAdmin = userIsAuthorized(user, ["juniper.self-serve.admin"]);

    const resolveSelectedTabIndex = useCallback(
        (tabId?: ProductTabIds): number => {
            switch (tabId) {
                case ProductTabIds.costMetadata:
                    return 0;
                case ProductTabIds.payoutRules:
                    return 1;
                default:
                    return 0;
            }
        },
        []
    );

    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(resolveSelectedTabIndex(focusedTab));
    const [shopifyAdminUrl, setShopifyAdminUrl] = useState<string | null>(null);
    const [hasPayoutRuleAssignments, setHasPayoutRuleAssignments] = useState<boolean>(true);
    const [hasCostMetadata, setHasCostMetadata] = useState<boolean>(true);

    const {
        loading: productLoading,
        error: productError,
        data: productData,
    } = useProductListingQuery(productListingId);

    // TODO: shopifyAccountId should live on ShopifyProduct, not CostMetadata.
    // A ShopifyProduct is ingested from exactly one Shopify store, but the current
    // schema places shopifyAccountId on CostMetadata — meaning multiple records for
    // the same product could theoretically have different values. In practice this
    // doesn't happen, but if it did, we'd silently use whichever record is returned
    // first and potentially link to the wrong Shopify store. The correct fix is to
    // add a shopifyAccountId column to ShopifyProduct (or derive it from the Hevo
    // ingestion source) and read it directly from there.
    useEffect(() => {
        if (!productData?.shopifyProductId) {
            return;
        }
        queryCostMetadataByShopifyProductId({
            shopifyProductId: productData.shopifyProductId,
            options: { take: 1 },
        }).then((costMetadata) => {
            setHasCostMetadata(costMetadata.length > 0);
            if (costMetadata.length > 0 && costMetadata[0].shopifyAccountId) {
                const domainPrefix = costMetadata[0].shopifyAccountId;
                setShopifyAdminUrl(
                    `${EDIT_IN_SHOPIFY_BASE_URL}${domainPrefix}/products/${productData.shopifyProductId}`
                );
            }
        }).catch((error) => {
            Sentry.captureException(error);
        });
    }, [productData?.shopifyProductId]);

    // Query hasPayoutRuleAssignments eagerly on page load for the tab ErrorIcon
    useEffect(() => {
        if (!productListingId) {
            return;
        }
        queryShopifyProductByProductListingId(productListingId)
            .then((shopifyProduct) => {
                const product = shopifyProduct as (typeof shopifyProduct & { hasPayoutRuleAssignments?: boolean }) | null;
                setHasPayoutRuleAssignments(product?.hasPayoutRuleAssignments ?? false);
            })
            .catch((error) => {
                Sentry.captureException(error);
            });
    }, [productListingId]);

    const onPayoutRuleDataLoaded = useCallback((hasData: boolean) => {
        setHasPayoutRuleAssignments(hasData);
    }, []);

    const tabs: TabInfo[] = useMemo(() => {
        const result: TabInfo[] = [];
        if (isAdmin) {
            result.push({
                label: "Cost Metadata",
                tabId: ProductTabIds.costMetadata,
                locked: false,
                component: <CostMetadataTab shopifyProductId={productData?.shopifyProductId} productListingId={productListingId} />,
                icon: !hasCostMetadata ? <ErrorIcon size={12} color="danger" /> : undefined,
            });

            result.push({
                label: "Payout Rules",
                tabId: ProductTabIds.payoutRules,
                locked: false,
                component: <PayoutRuleMappingTab productListingId={productListingId} storeId={productData?.store?.id} onDataLoaded={onPayoutRuleDataLoaded} />,
                icon: !hasPayoutRuleAssignments ? <ErrorIcon size={12} color="danger" /> : undefined,
            });

            if (shopifyAdminUrl) {
                result.push({
                    label: "Edit in Shopify",
                    tabId: ProductTabIds.editInShopify,
                    redirect: shopifyAdminUrl,
                    openInNewTab: true,
                });
            }
        }
        return result;
    }, [isAdmin, productData?.shopifyProductId, productData?.store?.id, shopifyAdminUrl, productListingId, hasPayoutRuleAssignments, hasCostMetadata, onPayoutRuleDataLoaded]);

    // Sync URL with the selected tab — handles initial load, tab changes, and invalid/missing tab paths
    useEffect(() => {
        if (productLoading || tabs.length === 0) {
            return;
        }

        const resolvedIndex = resolveSelectedTabIndex(focusedTab);
        setSelectedTabIndex(resolvedIndex);

        const targetTabId = tabs[resolvedIndex]?.tabId;
        if (targetTabId && focusedTab !== targetTabId) {
            NavigationHelper.pushPathname(
                history,
                generatePath(PRODUCT_ROUTE_PATH, {
                    productListingId,
                    focusedTab: targetTabId,
                })
            );
        }
    }, [productLoading, focusedTab, productListingId, tabs, history, resolveSelectedTabIndex]);

    if (productLoading && !productData && !productError) {
        return <CenteredSpinner/>;
    }

    if (productError || !productData) {
        return (
            <EmptyState
                title="Failed to load product"
                subtitle="Refresh the page to try again"
            >
                <ErrorIcon size={60}/>
            </EmptyState>
        );
    }

    return (
        <MultiTabView
            header={productData.name}
            tabs={tabs}
            selectedTabIndex={selectedTabIndex}
            onTabSelected={setSelectedTabIndex}
            fullPathFormat={PRODUCT_ROUTE_PATH}
            backButtonHeader="All Products"
            backButtonOnClick={() => history.push("/products")}
        />
    );
};

export const MultiTabProductPage = () => {
    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <MultiTabProductPageInternal />
        </RampartAuthorized>
    );
};
